import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { z } from "zod";
import AppleLogo from "../../assets/images/apple_logo_black.svg";
import GoogleLogo from "../../assets/images/google_logo.svg";
import heroBackground from "../../assets/images/welcome/hero.webp";
import doctor1 from "../../assets/images/welcome/doctor-1.webp";
import doctor2 from "../../assets/images/welcome/doctor-2.webp";
import doctor3 from "../../assets/images/welcome/doctor-3.webp";
import doctor4 from "../../assets/images/welcome/doctor-4.webp";
import doctor5 from "../../assets/images/welcome/doctor-5.webp";
import { AnimatedAskBar } from "../../components/ui/AnimatedAskBar";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Logo } from "../../components/ui/Logo";
import { useLocationState } from "../../domains/app/hooks/useLocationState";
import { useDesktopLogin } from "../../domains/auth/hooks/useDesktopLogin";
import { useTrackOnMount } from "../../hooks/use-track-on-mount";
import { gaEventSignupSheetOpened } from "../../tools/analytics/domains/onboarding.analytics";

const TRUSTED_BY_AVATARS = [
  doctor1,
  doctor2,
  doctor3,
  doctor4,
  doctor5,
] as const;

// Who sent the visitor here, for the door's analytics, and the address to
// show again when they come back from the code modal to change it. Absent on a
// cold arrival.
const locationStateSchema = z.object({
  email: z.string().nullable().default(null),
  surface: z
    .enum(["desktop_gate", "anonymous_gpt_wall", "anonymous_gpt_member"])
    .default("desktop_gate"),
});

/**
 * The desktop front door: the page every logged-out desktop visitor meets.
 * There is no welcome page on desktop. It has two doors, linked to each other:
 * - `signup` (Figma "D7.inscription.welcome"), on `/signup`: where the root,
 *   `/welcome` and every private page send;
 * - `login` (Figma "D7.connexion"), on `/login`: for who says they already
 *   have an account.
 *
 * The doors differ by their words only. The address decides (`check-email`)
 * whether the code that follows signs in or signs up, so a member who types
 * their address on the signup door is signed in. Continue asks for the code
 * and opens it in the auth gate modal over the app (`useDesktopLogin`).
 *
 * Its appearance is the impression: `signup_sheet_opened` with the surface,
 * once per session and surface (`useTrackOnMount`).
 */
export default function DesktopAuthPage({
  door,
}: {
  door: "signup" | "login";
}): React.JSX.Element {
  const { t } = useTranslation();
  const { email: prefilledEmail, surface } =
    useLocationState(locationStateSchema);
  const login = useDesktopLogin({ surface, prefilledEmail });
  const typedEmail = login.email.trim() === "" ? null : login.email.trim();

  useTrackOnMount(
    "desktop-auth-gate-shown",
    { status: "shown", key: surface },
    () => {
      gaEventSignupSheetOpened({ surface, is_invited_to_room: false });
    }
  );

  return (
    <div className="flex h-dvh w-full justify-center bg-ds-canvas">
      <div className="flex w-full max-w-desktop-shell">
        {/* `justify-center-safe`: on a window shorter than the form, the form
            starts at the top of its scroll box instead of overflowing above
            it, out of reach. */}
        <main className="flex w-desktop-auth-column shrink-0 flex-col items-center justify-center-safe overflow-y-auto p-8">
          <div className="flex w-full max-w-lg flex-col gap-7 p-11">
            <div className="flex flex-col gap-2">
              <h1 className="m-0 font-dm-sans text-desktop-display font-semibold text-ds-black">
                {door === "signup" ? t("Sign up to Juisci") : t("Welcome back")}
              </h1>
              <p className="m-0 font-dm-sans text-ui-body text-ds-slate-500">
                {door === "signup"
                  ? t(
                      "Ask a question. Juisci answers from the literature, and shows you the papers behind it."
                    )
                  : t(
                      "Log in with the email you use on Juisci. We'll send you a 6-digit code."
                    )}
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                variant="outline"
                size="md"
                disabled={login.isBusy}
                onClick={() => {
                  login.onProvider("apple");
                }}
              >
                <img className="h-4.5" src={AppleLogo} alt="" />
                {door === "signup"
                  ? t("Sign up with Apple")
                  : t("Log in with Apple")}
              </Button>
              <Button
                variant="outline"
                size="md"
                disabled={login.isBusy}
                onClick={() => {
                  login.onProvider("google");
                }}
              >
                <img className="h-4.5" src={GoogleLogo} alt="" />
                {door === "signup"
                  ? t("Sign up with Google")
                  : t("Log in with Google")}
              </Button>
            </div>

            <div className="flex items-center gap-0.5">
              <div className="h-px flex-1 bg-ds-slate-200" />
              <span className="px-1 font-dm-sans text-ui-caption font-medium text-ds-slate-300">
                {t("OR")}
              </span>
              <div className="h-px flex-1 bg-ds-slate-200" />
            </div>

            <form
              className="flex w-full flex-col gap-5"
              onSubmit={(e) => {
                e.preventDefault();
                login.onEmailSubmit();
              }}
            >
              <Input
                type="email"
                variant="ghost"
                className="border-ds-slate-200 bg-ds-white focus-within:border-ds-slate-500"
                placeholder={t("Personal or work email")}
                autoComplete="email"
                autoFocus
                disabled={login.isBusy}
                value={login.email}
                onChange={(e) => {
                  login.setEmail(e.target.value);
                }}
              />
              <Button
                type="submit"
                variant="primary"
                size="md"
                className="w-full text-ui-body"
                disabled={login.isBusy || login.email.trim() === ""}
              >
                {t("Continue")}
              </Button>
            </form>

            {/* The other door. `replace`: the two doors are one step of the
                way, so Back leaves the front door instead of swapping them. The
                surface rides along, and the address typed so far. */}
            {door === "signup" ? (
              <Link
                replace
                to={{
                  pathname: "/login",
                  state: { email: typedEmail, surface },
                }}
                className="flex items-center justify-center px-6 py-2.5 font-dm-sans text-ui-body font-semibold text-ds-slate-900 hover:underline"
              >
                {t("I already have an account")}
              </Link>
            ) : (
              <p className="m-0 text-center font-dm-sans text-ui-caption text-ds-slate-700">
                <Trans
                  i18nKey="New to Juisci? <signupLink>Create an account</signupLink>"
                  t={t}
                  components={{
                    signupLink: (
                      <Link
                        replace
                        to={{
                          pathname: "/signup",
                          state: { email: typedEmail, surface },
                        }}
                        className="text-ds-mint-800 underline"
                      />
                    ),
                  }}
                />
              </p>
            )}

            {/* Terms, privacy, and the healthcare-professional certification.
                This line is why the onboarding's intro slide does not run on
                desktop: continuing here IS the certification. */}
            <p className="m-0 text-center font-dm-sans text-ui-caption text-ds-slate-700">
              <Trans
                i18nKey="By continuing, you accept the <termsLink>Terms of Use</termsLink> and the <policyLink>Privacy Policy</policyLink>, and certify that you are a healthcare professional."
                t={t}
                components={{
                  termsLink: (
                    <Link
                      className="text-inherit hover:underline"
                      to="/terms?isPush=true&hideTitle=true"
                    />
                  ),
                  policyLink: (
                    <Link
                      className="text-inherit hover:underline"
                      to="/policy?isPush=true&hideTitle=true"
                    />
                  ),
                }}
              />
            </p>
          </div>
        </main>

        <aside className="flex min-w-0 flex-1 flex-col p-8">
          <div className="relative flex flex-1 flex-col justify-between overflow-clip rounded-xl p-8">
            <img
              src={heroBackground}
              alt=""
              className="absolute inset-0 size-full object-cover"
            />
            <Logo
              variant="no-tagline"
              tone="color"
              className="relative h-13 self-start"
            />
            <div className="relative flex flex-col gap-4">
              <h2 className="m-0 font-dm-sans text-desktop-hero font-semibold text-ds-white">
                <Trans
                  i18nKey="Built for clinicians. <serif>Evidence first</serif>"
                  t={t}
                  components={{
                    serif: <span className="font-petrona italic" />,
                  }}
                />
              </h2>
              <AnimatedAskBar tone="on-image" size="desktop" />
              <div className="flex flex-col gap-3">
                <div className="flex -space-x-3">
                  {TRUSTED_BY_AVATARS.map((avatar) => (
                    <img
                      key={avatar}
                      src={avatar}
                      alt=""
                      className="size-10 rounded-full object-cover"
                    />
                  ))}
                </div>
                <p className="m-0 font-dm-sans text-sm font-medium text-ds-white">
                  {t("Trusted by 35,000 verified healthcare professionals")}
                </p>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
